import { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface LiveGame {
  id: string;
  game_date: string;
  game_time: string;
  game_status: string;
  home_team_id: number | null;
  away_team_id: number | null;
  home_team_name: string;
  away_team_name: string;
  venue_name: string | null;
  sport_name: string | null;
  home_score: number | null;
  away_score: number | null;
}

interface UseLiveGamesOptions {
  stateProvince: string;
  country?: string;
  sportFilter?: string;
  limit?: number;
}

export const useLiveGames = (options: UseLiveGamesOptions) => {
  const { stateProvince, country = 'United States', sportFilter, limit = 10 } = options;
  const [games, setGames] = useState<LiveGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLiveGames = async () => {
      try {
        setLoading(true);
        setError(null);

        const today = new Date().toISOString().split('T')[0];
        
        // Fetch games that are in progress or scheduled for today
        const { data: gamesData, error: gamesError } = await supabase
          .from('games')
          .select(`
            id,
            game_date,
            game_time,
            game_status,
            home_team_id,
            away_team_id,
            venue_id,
            sport_id
          `)
          .in('game_status', ['in_progress', 'confirmed', 'scheduled'])
          .gte('game_date', today)
          .lte('game_date', today)
          .order('game_time', { ascending: true })
          .limit(limit);

        if (gamesError) throw gamesError;

        if (!gamesData || gamesData.length === 0) {
          setGames([]);
          setLoading(false);
          return;
        }

        // Get team IDs
        const teamIds = [...new Set([
          ...gamesData.map(g => g.home_team_id).filter(Boolean),
          ...gamesData.map(g => g.away_team_id).filter(Boolean)
        ])] as number[];

        // Fetch teams
        const { data: teamsData } = await supabase
          .from('teams')
          .select('team_id, team_name, state_province')
          .in('team_id', teamIds);

        const teamMap = new Map(teamsData?.map(t => [t.team_id, t]) || []);

        // Fetch venues
        const venueIds = gamesData.map(g => g.venue_id).filter(Boolean) as string[];
        let venueMap = new Map<string, any>();
        if (venueIds.length > 0) {
          const { data: venuesData } = await supabase
            .from('venues')
            .select('venue_id, venue_name, state_province')
            .in('venue_id', venueIds);
          venueMap = new Map(venuesData?.map(v => [v.venue_id, v]) || []);
        }

        // Fetch game results for scores
        const gameIds = gamesData.map(g => g.id);
        const { data: resultsData } = await supabase
          .from('game_results')
          .select('game_id, home_team_score, away_team_score')
          .in('game_id', gameIds);

        const resultMap = new Map(resultsData?.map(r => [r.game_id, r]) || []);

        // Fetch sports for names
        const sportIds = [...new Set(gamesData.map(g => g.sport_id).filter(Boolean))] as number[];
        let sportMap = new Map<number, string>();
        if (sportIds.length > 0) {
          const { data: sportsData } = await supabase
            .from('available_sports')
            .select('id, sport_name')
            .in('id', sportIds);
          sportMap = new Map(sportsData?.map(s => [s.id, s.sport_name]) || []);
        }

        // Filter by state and build response
        const enrichedGames: LiveGame[] = gamesData
          .map(game => {
            const homeTeam = teamMap.get(game.home_team_id!);
            const awayTeam = teamMap.get(game.away_team_id!);
            const venue = venueMap.get(game.venue_id || '');
            const result = resultMap.get(game.id);

            // Check if game is in the target state
            const gameState = venue?.state_province || homeTeam?.state_province || awayTeam?.state_province;
            
            return {
              id: game.id,
              game_date: game.game_date,
              game_time: game.game_time || '',
              game_status: game.game_status,
              home_team_id: game.home_team_id,
              away_team_id: game.away_team_id,
              home_team_name: homeTeam?.team_name || 'TBD',
              away_team_name: awayTeam?.team_name || 'TBD',
              venue_name: venue?.venue_name || null,
              sport_name: game.sport_id ? sportMap.get(game.sport_id) || null : null,
              home_score: result?.home_team_score || null,
              away_score: result?.away_team_score || null,
              _state: gameState,
            };
          })
          .filter(g => !stateProvince || (g as any)._state === stateProvince)
          .map(({ _state, ...rest }: any) => rest as LiveGame);

        setGames(enrichedGames);
      } catch (err) {
        console.error('Error fetching live games:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch games');
      } finally {
        setLoading(false);
      }
    };

    fetchLiveGames();
  }, [stateProvince, country, sportFilter, limit]);

  const inProgressGames = useMemo(() => 
    games.filter(g => g.game_status === 'in_progress'), 
    [games]
  );

  const upcomingGames = useMemo(() => 
    games.filter(g => ['confirmed', 'scheduled'].includes(g.game_status)), 
    [games]
  );

  return {
    games,
    inProgressGames,
    upcomingGames,
    loading,
    error,
  };
};
